/**
 * Message Context
 * Componente de mensagens alinhado ao Vover: posição topo centro,
 * fundo verde claro (success), ícone à esquerda, botão fechar à direita, 5s visível.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

const DEFAULT_DURATION_MS = 5000;
const ERROR_DURATION_MS = 8000;

interface MessageOptions {
  message: string;
  severity?: AlertColor;
  duration?: number | undefined;
}

interface MessageContextType {
  showMessage: (options: MessageOptions | string) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
}

const MessageContext = createContext<MessageContextType | undefined>(undefined);

export const MessageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('info');
  const [duration, setDuration] = useState(DEFAULT_DURATION_MS);

  const showMessage = useCallback((options: MessageOptions | string) => {
    if (typeof options === 'string') {
      setMessage(options);
      setSeverity('info');
      setDuration(DEFAULT_DURATION_MS);
    } else {
      setMessage(options.message);
      setSeverity(options.severity || 'info');
      setDuration(
        options.duration ?? (options.severity === 'error' ? ERROR_DURATION_MS : DEFAULT_DURATION_MS)
      );
    }
    setOpen(true);
  }, []);

  const showSuccess = useCallback(
    (msg: string, dur?: number) => {
      showMessage({
        message: msg,
        severity: 'success',
        ...(dur !== undefined && { duration: dur })
      });
    },
    [showMessage]
  );

  const showError = useCallback(
    (msg: string, dur?: number) => {
      showMessage({
        message: msg,
        severity: 'error',
        ...(dur !== undefined ? { duration: dur } : { duration: ERROR_DURATION_MS })
      });
    },
    [showMessage]
  );

  const showWarning = useCallback(
    (msg: string, dur?: number) => {
      showMessage({
        message: msg,
        severity: 'warning',
        ...(dur !== undefined && { duration: dur })
      });
    },
    [showMessage]
  );

  const showInfo = useCallback(
    (msg: string, dur?: number) => {
      showMessage({ message: msg, severity: 'info', ...(dur !== undefined && { duration: dur }) });
    },
    [showMessage]
  );

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const contextValue = useMemo(
    () => ({
      showMessage,
      showSuccess,
      showError,
      showWarning,
      showInfo
    }),
    [showMessage, showSuccess, showError, showWarning, showInfo]
  );

  return (
    <MessageContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          mt: 2,
          maxWidth: { xs: 'calc(100% - 32px)', sm: 560 },
          width: '100%'
        }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          variant="standard"
          elevation={2}
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            ...(severity === 'success' && {
              backgroundColor: 'rgb(229, 246, 230)',
              color: 'rgba(0, 0, 0, 0.87)',
              '& .MuiAlert-icon': {
                color: '#2e7d32'
              }
            })
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </MessageContext.Provider>
  );
};

export const useMessage = () => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error('useMessage must be used within a MessageProvider');
  }
  return context;
};
